// The hosted version in words (F8): the plan of a workspace, what a seat costs, what a plan limit says, and the seat
// question in the invite form. The API's plan object is mapped in billingApi.ts; everything here is pure so the
// sentences can be tested. Prices are euros excl. VAT; credits always stand next to euros (docs/brand/BRAND.md).
import { eurText, getPricing, type HostedPricing, moneyText } from "./money.ts";
import type { Invoice, PlanInfo } from "./sources.ts";

const INT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const ONE = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

/** The plan a workspace is on, as a word. A workspace that is not metered has none. */
export function planName(p: PlanInfo): string {
  if (p.billing !== "credits" || !p.plan) return "Unmetered";
  return p.plan === "team" ? "Team" : p.plan === "custom" ? "Custom" : "Free";
}

/** Seats that are paid for; a plan without a seat count (Free) has none. */
export const paidSeats = (p: PlanInfo): number => p.seatsPaid ?? 0;

/** "Team · 4 seats", "Free", "Custom · 6 seats". */
export function planTitle(p: PlanInfo): string {
  const name = planName(p);
  if (p.plan === "free" || !p.plan) return name;
  const n = paidSeats(p) || p.seatsUsed;
  return `${name} · ${INT.format(n)} ${n === 1 ? "seat" : "seats"}`;
}

/** "€24.00 per seat per month", excl. VAT being said once by the card that shows it. */
export const seatPriceText = (seatEur: number): string => `${eurText(seatEur)} per seat per month`;

/** "Each seat includes 300 credits · €15.00 a month." */
export const seatCreditsText = (seatCredits: number): string =>
  `Each seat includes ${moneyText(seatCredits, "long")} a month.`;

/** "Unused credits roll over, up to two months' worth (600 credits · €30.00)." */
export function rolloverText(
  p: Pick<PlanInfo, "rolloverCap">,
  months = getPricing().hosted?.rolloverMonths ?? 2,
): string {
  const words = ["zero", "one", "two", "three", "four", "five", "six"][months] ?? String(months);
  const cap = p.rolloverCap > 0 ? ` (${moneyText(p.rolloverCap, "long")})` : "";
  return `Unused credits roll over, up to ${words} ${months === 1 ? "month's" : "months'"} worth${cap}.`;
}

/** Bytes as GB for the storage meter: "0.4 GB", "12.4 GB", "230 GB". */
export function gbText(bytes: number): string {
  const gb = Math.max(0, bytes) / 1e9;
  if (gb > 0 && gb < 0.1) return "<0.1 GB";
  return `${gb >= 100 ? INT.format(gb) : ONE.format(gb)} GB`;
}

/** "12.4 GB of 200 GB"; a plan without a ceiling says what is used. */
export const storageText = (used: number, included: number | null): string =>
  included == null ? `${gbText(used)} used` : `${gbText(used)} of ${gbText(included)}`;

/** The share of the included storage that is used (0 to 1); null when there is no ceiling. */
export function storageShare(used: number, included: number | null): number | null {
  if (included == null || included <= 0) return null;
  return Math.min(1, Math.max(0, used / included));
}

/** "1 Nov" (and the year when it is not this one). */
export function shortDate(iso: string | null | undefined, now: Date = new Date(), timeZone?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const year = (x: Date) => new Intl.DateTimeFormat("en-GB", { timeZone, year: "numeric" }).format(x);
  const day = new Intl.DateTimeFormat("en-GB", { timeZone, day: "numeric", month: "short" }).format(d);
  return year(d) === year(now) ? day : `${day} ${year(d)}`;
}

/** The invoice date, always with its year: "1 Oct 2026". */
export function invoiceDate(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** What the renewal line says: when the plan renews, ends, or what happens after a failed payment. */
export function renewalLine(p: PlanInfo, now: Date = new Date(), timeZone?: string): string | null {
  if (p.plan === "free" || !p.plan) return null;
  // the note under the card says the rest (pastDueNote); the heading stays short
  if (p.status === "past_due") return "Payment failed";
  const when = shortDate(p.renewsAt, now, timeZone);
  if (!when) return null;
  return p.cancelAtPeriodEnd ? `Ends ${when}` : `Renews ${when}`;
}

/** The longer note under a failed payment: what we are doing and what happens at the end of the grace period. */
export function pastDueNote(p: PlanInfo, now: Date = new Date(), timeZone?: string): string | null {
  if (p.status !== "past_due") return null;
  const until = shortDate(p.graceUntil, now, timeZone);
  return `We could not take the payment and are trying again. Update your card under Billing details${
    until ? `. If it is not paid by ${until}, the workspace moves to Free` : ""
  }. Nothing is deleted: everything stays readable, only new productions, final renders and uploads above the Free limits wait.`;
}

/** The note when a plan is cancelled at the end of the period. */
export function cancelNote(p: PlanInfo, now: Date = new Date(), timeZone?: string): string | null {
  if (p.plan !== "team" || !p.cancelAtPeriodEnd || p.status === "past_due") return null;
  const when = shortDate(p.renewsAt, now, timeZone);
  return `Your plan ends${when ? ` on ${when}` : " at the end of the period"} and the workspace moves to Free. Nothing is deleted.`;
}

/** What Free includes, one fact per line, credits next to euros. */
export function freeFacts(h: HostedPricing | undefined): string[] {
  const f = h?.free ?? { credits: 100, productions: 1, final: false, storageGb: 5 };
  return [
    `${moneyText(f.credits, "long")}, once`,
    `${f.productions === 1 ? "One production" : `${INT.format(f.productions)} productions`}`,
    f.final ? "Drafts, stills and final renders" : "Drafts and stills, no final renders",
    `${INT.format(f.storageGb)} GB of storage`,
  ];
}

/** What a new Free workspace should know, in one sentence: its limits, and where seats are. */
export function freeIntro(h: HostedPricing | undefined): string {
  const f = h?.free ?? { credits: 100, productions: 1, final: false, storageGb: 5 };
  const prods = f.productions === 1 ? "one production" : `${INT.format(f.productions)} productions`;
  return `You start on Free: ${prods}, drafts and stills${f.final ? "" : ", no final renders"}, ${INT.format(f.storageGb)} GB. Seats for your crew come with the Team plan, in Credits & plan.`;
}

// ── seats: the stepper and the invite form ───────────────────────────────────────────────────────────────

/** The fewest seats the plan can drop to: the people who hold one (and at least one). */
export const minSeats = (p: PlanInfo): number => Math.max(1, p.seatsUsed);

/** What a change of seats means, in a sentence, with the money: adding is prorated, removing waits for renewal. */
export function seatChangeText(
  p: PlanInfo,
  to: number,
  now: Date = new Date(),
  timeZone?: string,
): string | null {
  const from = paidSeats(p);
  if (to === from) return null;
  const diff = Math.abs(to - from);
  const credits = diff * p.seatCredits;
  const eur = diff * p.seatEur;
  const noun = diff === 1 ? "seat" : "seats";
  if (to > from) {
    return `Adding ${INT.format(diff)} ${noun} costs ${eurText(eur)} a month, charged now for the rest of this period. It brings ${moneyText(credits, "long")} a month.`;
  }
  const when = shortDate(p.renewsAt, now, timeZone);
  return `Removing ${INT.format(diff)} ${noun} takes effect${when ? ` on ${when}` : " at the next renewal"}. You keep the seats until then.`;
}

/** What the invite form knows about the seat a role needs. */
export type SeatNeed =
  /** nothing to say: not a metered workspace, or the plan is not known yet */
  | { kind: "none" }
  /** a reviewer or viewer: free */
  | { kind: "free" }
  /** a seat is available */
  | { kind: "available"; left: number }
  /** Team and Custom: every seat is taken; the owner can add one (Team), others ask the owner */
  | { kind: "full"; plan: "team" | "custom"; seatEur: number; seatCredits: number; canAdd: boolean }
  /** Free: seats come with Team */
  | { kind: "get_seats"; seatEur: number; seatCredits: number };

/** Does this role take a seat on this workspace, and is one left? */
export function seatNeed(p: PlanInfo | null, role: string, seatRoles?: readonly string[]): SeatNeed {
  if (p?.billing !== "credits" || !p.plan) return { kind: "none" };
  const roles = seatRoles ?? getPricing().hosted?.seatRoles ?? ["owner", "producer", "director", "maker"];
  if (!roles.includes(role)) return { kind: "free" };
  const { seatEur, seatCredits } = p;
  if (p.plan === "free") return { kind: "get_seats", seatEur, seatCredits };
  const paid = p.seatsPaid ?? p.seatsUsed;
  if (p.seatsUsed < paid) return { kind: "available", left: paid - p.seatsUsed };
  return {
    kind: "full",
    plan: p.plan,
    seatEur,
    seatCredits,
    canAdd: p.plan === "team" && p.canManage && p.billingAvailable,
  };
}

/** The extra seat, in a sentence: price, what comes with it, and that it is prorated. */
export const extraSeatText = (seatEur: number, seatCredits: number): string =>
  `An extra seat costs ${eurText(seatEur)} a month excl. VAT, charged now for the rest of the period. It comes with ${moneyText(seatCredits, "long")} a month.`;

// ── plan limits ──────────────────────────────────────────────────────────────────────────────────────────

/** The words of the link that ends a plan-limit sentence; `ErrorText` turns them into a link to #/credits. */
export const CREDITS_AND_PLAN = "Credits & plan";

/**
 * A 402 plan_limit as one quiet sentence ending in "Credits & plan". Null for any other error. The numbers come from
 * the server's prices when they are loaded; without them the sentence says no numbers.
 */
export function planLimitText(
  e: unknown,
  hosted: HostedPricing | undefined = getPricing().hosted,
): string | null {
  const code = e && typeof e === "object" && "code" in e ? (e as { code: unknown }).code : null;
  if (code !== "plan_limit") return null;
  const d = (e as { details?: { limit?: unknown; plan?: unknown } }).details;
  const limit = typeof d?.limit === "string" ? d.limit : "";
  const onFree = d?.plan === "free" || d?.plan == null;
  const link = `See ${CREDITS_AND_PLAN}.`;
  switch (limit) {
    case "final":
      return `Final renders come with the Team plan. ${link}`;
    case "productions": {
      const n = hosted?.free.productions ?? 1;
      return `Free includes ${n === 1 ? "one production" : `${n} productions`}. More come with the Team plan. ${link}`;
    }
    case "storage": {
      const gb = hosted?.free.storageGb;
      return onFree
        ? `Storage is full${gb ? ` (${gb} GB on Free)` : ""}. More space comes with the Team plan. ${link}`
        : `Storage is full for this plan. ${link}`;
    }
    case "seats": {
      if (onFree) return `Seats come with the Team plan. Get seats in ${CREDITS_AND_PLAN}.`;
      const price = hosted
        ? ` (${eurText(hosted.seatEur)} a month with ${moneyText(hosted.seatCredits, "long")})`
        : "";
      return `All seats are in use. Add a seat${price} in ${CREDITS_AND_PLAN}.`;
    }
    case "packs":
      return `Credit packs come with the Team plan. Get seats in ${CREDITS_AND_PLAN}.`;
    default:
      return `This is above what your plan includes. ${link}`;
  }
}

/** The 409 seats_in_use of a seat change: someone holds each of those seats. */
export const SEATS_IN_USE_TEXT =
  "Those seats are in use. Make someone a reviewer or viewer (both free), or remove them, and then take the seat away.";

// ── invoices ─────────────────────────────────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  paid: "Paid",
  open: "Open",
  draft: "Draft",
  void: "Void",
  uncollectible: "Unpaid",
};

export const invoiceStatus = (status: string): string =>
  STATUS_LABEL[status] ?? (status ? status.charAt(0).toUpperCase() + status.slice(1) : "");

/** The invoice's description, or a plain one for its kind. */
export const invoiceWhat = (i: Pick<Invoice, "kind" | "description">): string =>
  i.description?.trim() || (i.kind === "pack" ? "Credit pack" : "Team plan");

/** What a month of the plan costs now, for the top of the card: seats × price, excl. VAT. */
export const planMonthlyEur = (p: PlanInfo): number => paidSeats(p) * p.seatEur;

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  producer: "Producer",
  director: "Director",
  maker: "Maker",
  reviewer: "Reviewer",
  viewer: "Viewer",
};

/** A role in a plain name ("producer" → "Producer"). */
export const roleLabel = (role: string): string => ROLE_LABELS[role] ?? role;

// ── coming back from Stripe ──────────────────────────────────────────────────────────────────────────────

/** What Stripe Checkout sent the browser back with: `?billing=done` or `?billing=cancel`. Null for any other address. */
export function billingReturn(search: string): "done" | "cancel" | null {
  const v = new URLSearchParams(search).get("billing");
  return v === "done" || v === "cancel" ? v : null;
}

/** The address without `billing=…`, the rest of the query and the #/route kept. */
export function withoutBillingParam(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("billing");
  return url.toString();
}

/** The webhook can lag: look at the plan and the credits again a few times after a payment. */
export const BILLING_FOLLOW_UPS_MS = [1500, 4000, 9000, 20000];

export const BILLING_RETURN_TEXT = {
  done: "Payment received. Your plan updates in a moment.",
  cancel: "Checkout was closed and nothing was charged.",
} as const;
